import React from 'react';
import Badge from '../ds/Badge';
import Button from '../ds/Button';
import Card from '../ds/Card';
import Logo, { Isotype } from '../ds/Logo';
import StatTile from '../ds/StatTile';
import Avatar from '../ds/Avatar';
import ScoreMeter from '../ds/ScoreMeter';
import Input from '../ds/Input';
import Select from '../ds/Select';
import Checkbox from '../ds/Checkbox';
import Switch from '../ds/Switch';
import Tabs from '../ds/Tabs';
import Icon, { ICON_NAMES } from '../lib/Icon';
import StatusPill from '../shared/StatusPill';
import AssessmentStatusPill from '../shared/AssessmentStatusPill';
import AssessmentTimeline from '../shared/AssessmentTimeline';
import StatusLegend from '../shared/StatusLegend';
import Stat from '../shared/Stat';
import ConfidentialBanner from '../shared/ConfidentialBanner';
import ConfidentialityNote from '../shared/ConfidentialityNote';
import { useUser } from '../../contexts/ContextUser';

const RAMPS = {
  neutral: [0, 50, 100, 200, 300, 400, 500, 700, 800, 900],
  science: [50, 100, 200, 300, 400, 500, 600, 700],
  human: [50, 100, 200, 300, 400, 500, 600, 700, 800],
};

const TIMELINE = [
  { id: 'a', name: 'Baseline 360', status: 'complete', window: 'Jan 15 – Feb 1' },
  { id: 'b', name: 'Mid-point Pulse', status: 'active', window: 'Mar 1 – Mar 15' },
  { id: 'c', name: 'Final 360', status: 'scheduled', window: 'Opens Apr 1' },
  { id: 'd', name: 'Capstone', status: 'locked', window: 'Sequenced' },
];

function Section({ title, children }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Isotype size={13} style={{ color: 'var(--science-400)' }} />
        <h3 style={{ margin: 0, fontSize: 'var(--text-h4)' }}>{title}</h3>
      </div>
      <Card elevation="sm" style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
        {children}
      </Card>
    </section>
  );
}

/** Admin-only design-system QA page: every primitive × variant, for staging sign-off. */
export default function DesignSpecimens() {
  const { user } = useUser();
  if (!user.admin) {
    return (
      <div style={{ padding: '90px 36px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 'var(--text-h3)' }}>Admins only</h1>
        <p className="cc-prose" style={{ color: 'var(--ink-soft)' }}>The design specimen page is a QA surface for administrators.</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '30px 36px 80px', display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1240, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <div>
        <div className="cc-eyebrow" style={{ marginBottom: 6 }}>Design system · QA</div>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', letterSpacing: 'var(--track-display)' }}>Specimens</h1>
      </div>

      <Section title="Colors">
        {Object.entries(RAMPS).map(([ramp, steps]) => (
          <div key={ramp} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="cc-eyebrow">{ramp}</span>
            <div style={{ display: 'flex' }}>
              {steps.map((s) => (
                <div key={s} title={`--${ramp}-${s}`} style={{ width: 42, height: 42, background: `var(--${ramp}-${s})`, border: '1px solid var(--line)', marginLeft: -1 }} />
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Typography">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 'var(--text-display)', fontWeight: 'var(--w-bold)', letterSpacing: 'var(--track-display)', lineHeight: 'var(--lh-display)' }}>Display 56</span>
          <h1 style={{ margin: 0 }}>Heading one</h1>
          <h2 style={{ margin: 0 }}>Heading two</h2>
          <h3 style={{ margin: 0 }}>Heading three</h3>
          <p style={{ margin: 0 }}>Body — DM Sans {`{`}400/500/700{`}`}, tracking −0.02em.</p>
          <p className="cc-prose" style={{ margin: 0 }}>Editorial serif — Bitter carries long-form warmth.</p>
          <span className="cc-eyebrow">02 — Eyebrow label</span>
        </div>
      </Section>

      <Section title="Brand">
        <Logo variant="lockup" size={22} />
        <Logo variant="wordmark" size={20} />
        <Logo variant="full" size={18} />
        <Isotype size={28} style={{ color: 'var(--science-400)' }} />
      </Section>

      <Section title="Buttons">
        {['primary', 'secondary', 'outline', 'ghost', 'ink'].map((v) => (
          <Button key={v} variant={v}>{v}</Button>
        ))}
        <Button size="sm">small</Button>
        <Button size="lg">large</Button>
        <Button disabled>disabled</Button>
        <Button iconLeft={<Icon name="plus" size={18} />}>with icon</Button>
      </Section>

      <Section title="Badges">
        {['science', 'human', 'neutral', 'ink', 'solid'].map((t) => (
          <Badge key={t} tone={t}>{t}</Badge>
        ))}
        <Badge tone="science" dot>with dot</Badge>
      </Section>

      <Section title="Cards">
        <Card elevation="sm" style={{ width: 180 }}>elevation sm</Card>
        <Card elevation="md" style={{ width: 180 }}>elevation md</Card>
        <Card tone="sunk" style={{ width: 180 }}>tone sunk</Card>
        <Card tone="ink" style={{ width: 180 }}>tone ink</Card>
        <Card interactive style={{ width: 180 }}>interactive</Card>
      </Section>

      <Section title="Data">
        <StatTile label="Overall coachability" value={77} tone="science" caption="Cohort mean, all factors" />
        <StatTile label="Completion" value={83} suffix="%" tone="human" />
        <div style={{ minWidth: 260, flex: 1 }}>
          <ScoreMeter label="Humility" value={82} />
          <ScoreMeter label="Cool" value={44} tone="human" style={{ marginTop: 12 }} />
        </div>
        <Card elevation="sm" pad="0" style={{ display: 'flex', alignItems: 'stretch' }}>
          <Stat quiet value={4} label="Active engagements" icon="layers" />
          <div style={{ width: 1, background: 'var(--line)' }} />
          <Stat quiet value={87} suffix="%" label="Avg completion" icon="target" tone="var(--science-700)" trend={12} />
        </Card>
      </Section>

      <Section title="Forms">
        <Input label="Engagement name" hint="Shown to participants" style={{ minWidth: 240 }} />
        <Input label="With error" error="Required" style={{ minWidth: 240 }} />
        <Select label="Assessment type" options={['Coachability 360', 'Pulse check']} style={{ minWidth: 240 }} />
        <Checkbox label="Send invites automatically" defaultChecked />
        <Switch label="Auto-sequence" defaultChecked />
        <Tabs tabs={[{ id: 'a', label: 'Active', count: 3 }, { id: 'b', label: 'Completed', count: 2 }]} style={{ width: '100%' }} />
      </Section>

      <Section title="Status">
        <StatusPill status="active" />
        <StatusPill status="completed" />
        <StatusPill status="archived" />
        <AssessmentStatusPill status="complete" />
        <AssessmentStatusPill status="active" />
        <AssessmentStatusPill status="scheduled" />
        <AssessmentStatusPill status="locked" />
        <StatusLegend />
        <div style={{ width: '100%' }}>
          <AssessmentTimeline assessments={TIMELINE} size="lg" />
        </div>
      </Section>

      <Section title="Access">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
          <ConfidentialBanner />
          <ConfidentialityNote />
        </div>
      </Section>

      <Section title="Avatars">
        <Avatar name="Sarah Montgomery" size="sm" />
        <Avatar name="Priya Shah" size="md" tone="human" />
        <Avatar name="Jordan Avery" size="lg" tone="ink" />
      </Section>

      <Section title="Icons">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          {ICON_NAMES.map((n) => (
            <span key={n} title={n} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 64, color: 'var(--ink-soft)' }}>
              <Icon name={n} size={20} />
              <span style={{ fontSize: 10, color: 'var(--ink-faint)' }}>{n}</span>
            </span>
          ))}
        </div>
      </Section>
    </div>
  );
}
